import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Button,
    Grid,
    Typography,
    TablePagination,
    Stack,
    Tooltip,
    Chip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import {
    PictureAsPdf as PictureAsPdfIcon,
    ArrowBack as ArrowBackIcon,
    InfoOutlined as InfoOutlinedIcon,
    FileDownload as FileDownloadIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import inventarioInformes, {
    formatStockDepositoNumero,
    type StockDepositoRow,
} from '../../services/inventarioInformes';
import productoService from '../../services/productoService';
import kitService from '../../services/kitService';
import { esTipoKit } from '../../utils/stockUmVenta';
import productoDepositoService, {
  claveProductoDeposito,
} from '../../services/productoDepositoService';
import ProductoInfo360Dialog from './info360/ProductoInfo360Dialog';
import ProductoInfo360ListButton from './info360/ProductoInfo360ListButton';
import PDFViewer from '../../components/shared/PDFViewer';

interface StockDeposito extends StockDepositoRow {
    coberturaDias?: number | null;
    demandaMensual6m?: number | null;
    leadTimeDias?: number | null;
    ubicacionFisica?: string;
}

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}

interface StockFilters {
    codigoArticulo: string;
    descripcion: string;
    nroUbicacion: string;
}

const EMPTY_FILTERS: StockFilters = {
    codigoArticulo: '',
    descripcion: '',
    nroUbicacion: '',
};

const TOOLTIP_CANTIDAD = 'Stock físico real en el depósito.';
const TOOLTIP_ASIGNADO = 'Cantidades pendientes de pedidos de venta.';
const TOOLTIP_DISPONIBLE = 'Stock disponible: físico real − asignado + órdenes de compra pendientes.';

const hoyIso = () => new Date().toISOString().slice(0, 10);

const nroUbicacionFiltro = (nroUbicacion: string) =>
    nroUbicacion ? Number(nroUbicacion) : undefined;

const StockPorDeposito: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { enqueueSnackbar } = useSnackbar();

    const [stock, setStock] = useState<StockDeposito[]>([]);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [filters, setFilters] = useState<StockFilters>(EMPTY_FILTERS);
    const [applied, setApplied] = useState<StockFilters>(EMPTY_FILTERS);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalCantidad, setTotalCantidad] = useState(0);
    const [totalAsignado, setTotalAsignado] = useState(0);
    const [totalDisponible, setTotalDisponible] = useState(0);
    const [info360Articulo, setInfo360Articulo] = useState<string | null>(null);
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const previewPdfUrlRef = useRef<string | null>(null);
    previewPdfUrlRef.current = previewPdfUrl;

    useEffect(() => {
        return () => {
            if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
        };
    }, []);

    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
            }
        };
        void loadUbicaciones();
    }, [enqueueSnackbar]);

    const ubicacionOptions = useMemo(
        () =>
            ubicaciones.map((ubicacion) => ({
                value: ubicacion.nroUbicacion,
                label: `${ubicacion.nroUbicacion} - ${ubicacion.nombreUbicacion}`,
            })),
        [ubicaciones],
    );

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getStockPorDeposito({
                codigoArticulo: applied.codigoArticulo || undefined,
                descripcion: applied.descripcion || undefined,
                nroUbicacion: nroUbicacionFiltro(applied.nroUbicacion),
                page: page + 1,
                pageSize: rowsPerPage,
            });
            const rows: StockDeposito[] = (response.data ?? []) as StockDeposito[];
            try {
                const kitKeys = new Map<string, number[]>();
                for (const row of rows) {
                    if (!esTipoKit(row.tipoArticulo)) continue;
                    const key = row.codigoArticulo.trim().toUpperCase();
                    const list = kitKeys.get(key) ?? [];
                    list.push(row.nroUbicacion);
                    kitKeys.set(key, list);
                }
                await Promise.all(
                    Array.from(kitKeys.entries()).map(async ([codigoUpper, ubicacionesKit]) => {
                        const codigo = rows.find(
                            (r) => r.codigoArticulo.trim().toUpperCase() === codigoUpper,
                        )?.codigoArticulo;
                        if (!codigo) return;
                        const uniqueUbic = Array.from(new Set(ubicacionesKit));
                        await Promise.all(
                            uniqueUbic.map(async (nroUbicacion) => {
                                try {
                                    const kitStock = await kitService.getStockKit(codigo, nroUbicacion);
                                    for (const row of rows) {
                                        if (
                                            row.codigoArticulo.trim().toUpperCase() === codigoUpper &&
                                            row.nroUbicacion === nroUbicacion
                                        ) {
                                            row.disponibleDeposito = Number(kitStock.disponible) || 0;
                                        }
                                    }
                                } catch {
                                    /* dejar disponible físico */
                                }
                            }),
                        );
                    }),
                );
            } catch (kitError) {
                console.error('Error al calcular stock de kits:', kitError);
            }
            try {
                const semaforos = await productoService.getProductosInfo360Semaforo(
                    rows.map((r) => r.codigoArticulo),
                );
                const byCodigo = new Map(
                    semaforos.map((s) => [s.codigoArticulo.trim().toUpperCase(), s]),
                );
                for (const row of rows) {
                    const s = byCodigo.get(row.codigoArticulo.trim().toUpperCase());
                    if (!s) continue;
                    (row as StockDeposito).coberturaDias = s.coberturaDias;
                    (row as StockDeposito).demandaMensual6m = s.demandaMensual6m;
                    (row as StockDeposito).leadTimeDias = s.leadTimeDias;
                }
            } catch (semaforoError) {
                console.error('Error al cargar semáforo Info 360:', semaforoError);
            }
            try {
                const textos = await productoDepositoService.resolverUbicacionesFisicas(
                    rows.map((r: StockDeposito) => ({
                        codigoArticulo: r.codigoArticulo,
                        nroUbicacion: r.nroUbicacion,
                    })),
                );
                for (const row of rows) {
                    row.ubicacionFisica =
                        textos.get(claveProductoDeposito(row.codigoArticulo, row.nroUbicacion)) || '';
                }
            } catch (ubicacionError) {
                console.error('Error al cargar ubicaciones físicas:', ubicacionError);
            }
            setStock(rows);
            setTotalCount(response.totalCount ?? 0);
            setTotalCantidad(response.totalCantidad ?? 0);
            setTotalAsignado(response.totalAsignado ?? 0);
            setTotalDisponible(response.totalDisponible ?? 0);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            enqueueSnackbar('Error al cargar los datos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applied, page, rowsPerPage, enqueueSnackbar]);

    useEffect(() => {
        void loadData();
    }, [loadData]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = () => {
        setPage(0);
        setApplied({ ...filters });
    };

    const handleClearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(0);
    };

    const handleFilterKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    const handleClosePdfPreview = () => {
        setPreviewPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const handleGeneratePdf = async () => {
        try {
            setLoading(true);
            const blob = await inventarioInformes.getStockPorDepositoPdf({
                codigoArticulo: applied.codigoArticulo || undefined,
                descripcion: applied.descripcion || undefined,
                nroUbicacion: nroUbicacionFiltro(applied.nroUbicacion),
            });

            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }

            const pdfBlob =
                blob instanceof Blob && blob.type === 'application/pdf'
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            setPreviewPdfUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
        } catch (error: unknown) {
            console.error('Error al generar PDF:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const errorMessage = err.response?.data?.error || 'Error al generar el PDF';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getStockPorDeposito({
                codigoArticulo: applied.codigoArticulo || undefined,
                descripcion: applied.descripcion || undefined,
                nroUbicacion: nroUbicacionFiltro(applied.nroUbicacion),
            });
            const data = response.data ?? [];
            if (data.length === 0) {
                enqueueSnackbar('No hay datos para exportar', { variant: 'info' });
                return;
            }
            const sheetRows: Array<Record<string, string | number>> = data.map((row, index) => ({
                'N°': index + 1,
                Código: row.codigoArticulo,
                Descripción: row.descripcion,
                'Nro. Ubicación': row.nroUbicacion,
                'Nombre Ubicación': row.nombreUbicacion,
                Cantidad: row.cantidadDeposito,
                Asignado: row.cantidadAsignada,
                Disponible: row.disponibleDeposito,
            }));
            sheetRows.push({
                'N°': '',
                Código: '',
                Descripción: '',
                'Nro. Ubicación': '',
                'Nombre Ubicación': 'Totales',
                Cantidad: response.totalCantidad,
                Asignado: response.totalAsignado,
                Disponible: response.totalDisponible,
            });
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Stock por depósito');
            XLSX.writeFile(wb, `stock-por-deposito-${hoyIso()}.xlsx`);
            enqueueSnackbar('Excel exportado correctamente', { variant: 'success' });
        } catch (error: unknown) {
            console.error('Error al exportar Excel:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const errorMessage = err.response?.data?.error || 'Error al exportar a Excel';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRowNavigateMovimientos = (row: StockDeposito) => {
        const params = new URLSearchParams();
        params.set('codigoArticulo', row.codigoArticulo);
        params.set('deposito', String(row.nroUbicacion));
        navigate(`/inventario/informes/movimientos-stock?${params.toString()}`);
    };

    const renderHeaderConTooltip = (label: string, tooltip: string, align: 'left' | 'right' = 'right') => (
        <Tooltip title={tooltip} arrow placement="top">
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'help',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    width: '100%',
                }}
            >
                {label}
                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Box>
        </Tooltip>
    );

    const renderCardCampo = (label: string, valor: React.ReactNode, tooltip?: string) => (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
                {tooltip ? (
                    <Tooltip title={tooltip} arrow placement="top">
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            {label}
                            <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        </Box>
                    </Tooltip>
                ) : (
                    label
                )}
            </Typography>
            <Typography variant="body2">{valor}</Typography>
        </Box>
    );

    const renderTotalsBlock = () => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Grid container spacing={1.5}>
                <Grid item xs={4}>
                    {renderCardCampo('Total cantidad', formatStockDepositoNumero(totalCantidad), TOOLTIP_CANTIDAD)}
                </Grid>
                <Grid item xs={4}>
                    {renderCardCampo('Total asignado', formatStockDepositoNumero(totalAsignado), TOOLTIP_ASIGNADO)}
                </Grid>
                <Grid item xs={4}>
                    {renderCardCampo('Total disponible', formatStockDepositoNumero(totalDisponible), TOOLTIP_DISPONIBLE)}
                </Grid>
            </Grid>
        </Paper>
    );

    const renderCardsMobile = () => {
        if (!stock || stock.length === 0) {
            return (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No hay datos disponibles
                </Typography>
            );
        }

        return (
            <Stack spacing={2}>
                {stock.map((row) => (
                    <Paper
                        key={`${row.codigoArticulo}-${row.nroUbicacion}`}
                        variant="outlined"
                        onClick={() => handleRowNavigateMovimientos(row)}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            cursor: 'pointer',
                            '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                        }}
                    >
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                {renderCardCampo('Código', row.codigoArticulo)}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Descripción', row.descripcion)}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Nro. Ubicación', row.nroUbicacion)}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Nombre Ubicación', row.nombreUbicacion)}
                            </Grid>
                            <Grid item xs={12}>
                                {renderCardCampo('Ubicación física', row.ubicacionFisica || '—')}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('Cantidad', formatStockDepositoNumero(row.cantidadDeposito), TOOLTIP_CANTIDAD)}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('Asignado', formatStockDepositoNumero(row.cantidadAsignada), TOOLTIP_ASIGNADO)}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('Disponible', formatStockDepositoNumero(row.disponibleDeposito), TOOLTIP_DISPONIBLE)}
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end">
                                    <ProductoInfo360ListButton
                                        producto={{
                                            codigoArticulo: row.codigoArticulo,
                                            cantidad: row.disponibleDeposito,
                                            coberturaDias: row.coberturaDias,
                                            demandaMensual6m: row.demandaMensual6m,
                                            leadTimeDias: row.leadTimeDias,
                                        }}
                                        onClick={() => setInfo360Articulo(row.codigoArticulo)}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                {stock.length > 0 && renderTotalsBlock()}
            </Stack>
        );
    };

    const pagination = (
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
                mt: isMobile ? 2 : 0,
                '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1,
                },
            }}
        />
    );

    const exportButtons = (
        <>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleGeneratePdf()}
                disabled={loading}
                fullWidth={isMobile}
                sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                }}
            >
                Generar PDF
            </Button>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                disabled={loading}
                fullWidth={isMobile}
                sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                }}
            >
                Exportar Excel
            </Button>
        </>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 1 }}
                    >
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/inventario/informes')}
                            sx={{ textTransform: 'none' }}
                        >
                            Informes
                        </Button>
                        <Box>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{ fontWeight: 600, color: '#1976d2' }}
                            >
                                Stock por Depósito
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Consulte el stock físico, asignado y disponible por depósito.
                            </Typography>
                        </Box>
                    </Stack>

                    <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Código Artículo"
                                        value={filters.codigoArticulo}
                                        onChange={(e) => setFilters({ ...filters, codigoArticulo: e.target.value })}
                                        onKeyDown={handleFilterKeyDown}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Descripción"
                                        value={filters.descripcion}
                                        onChange={(e) => setFilters({ ...filters, descripcion: e.target.value })}
                                        onKeyDown={handleFilterKeyDown}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <SearchableSelect<number>
                                        label="Ubicación"
                                        size="small"
                                        value={filters.nroUbicacion ? Number(filters.nroUbicacion) : null}
                                        onChange={(val) =>
                                            setFilters({
                                                ...filters,
                                                nroUbicacion: val != null ? String(val) : '',
                                            })
                                        }
                                        options={ubicacionOptions}
                                        allowEmpty
                                        emptyOptionLabel="Todas"
                                    />
                                </Grid>
                            </Grid>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                mt={2}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="space-between"
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSearch}
                                        disabled={loading}
                                        fullWidth={isMobile}
                                    >
                                        Buscar
                                    </Button>
                                    <Button
                                        variant="text"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearFilters}
                                        disabled={loading}
                                        fullWidth={isMobile}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Limpiar filtros
                                    </Button>
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    {exportButtons}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    {totalCount > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                label={`${totalCount.toLocaleString('es-AR')} registro${totalCount === 1 ? '' : 's'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    )}

                    <Box sx={{ position: 'relative' }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.6)',
                                    zIndex: 1,
                                    borderRadius: 2,
                                    minHeight: 120,
                                }}
                            >
                                <CircularProgress size={36} />
                            </Box>
                        )}

                        {isMobile ? (
                            <Box>
                                {renderCardsMobile()}
                                {pagination}
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Nro. Ubicación</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Nombre Ubicación</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Ubicación física</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {renderHeaderConTooltip('Cantidad', TOOLTIP_CANTIDAD)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {renderHeaderConTooltip('Asignado', TOOLTIP_ASIGNADO)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {renderHeaderConTooltip('Disponible', TOOLTIP_DISPONIBLE)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stock && stock.length > 0 ? (
                                            stock.map((row) => (
                                                <TableRow
                                                    key={`${row.codigoArticulo}-${row.nroUbicacion}`}
                                                    hover
                                                    onClick={() => handleRowNavigateMovimientos(row)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell>{row.codigoArticulo}</TableCell>
                                                    <TableCell>{row.descripcion}</TableCell>
                                                    <TableCell>{row.nroUbicacion}</TableCell>
                                                    <TableCell>{row.nombreUbicacion}</TableCell>
                                                    <TableCell>{row.ubicacionFisica || '—'}</TableCell>
                                                    <TableCell align="right">
                                                        {formatStockDepositoNumero(row.cantidadDeposito)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatStockDepositoNumero(row.cantidadAsignada)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatStockDepositoNumero(row.disponibleDeposito)}
                                                    </TableCell>
                                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                        <ProductoInfo360ListButton
                                                            producto={{
                                                                codigoArticulo: row.codigoArticulo,
                                                                cantidad: row.disponibleDeposito,
                                                                coberturaDias: row.coberturaDias,
                                                                demandaMensual6m: row.demandaMensual6m,
                                                                leadTimeDias: row.leadTimeDias,
                                                            }}
                                                            onClick={() => setInfo360Articulo(row.codigoArticulo)}
                                                            compact
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center">
                                                    No hay datos disponibles
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    {stock.length > 0 && (
                                        <TableFooter>
                                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                <TableCell colSpan={5} sx={{ fontWeight: 600 }}>
                                                    Totales
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatStockDepositoNumero(totalCantidad)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatStockDepositoNumero(totalAsignado)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatStockDepositoNumero(totalDisponible)}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableFooter>
                                    )}
                                </Table>
                                {pagination}
                            </TableContainer>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <ProductoInfo360Dialog
                open={info360Articulo != null}
                codigoArticulo={info360Articulo}
                onClose={() => setInfo360Articulo(null)}
            />

            {previewPdfUrl ? (
                <PDFViewer
                    pdfUrl={previewPdfUrl}
                    onClose={handleClosePdfPreview}
                    title="Stock por depósito"
                    documentId="stock-deposito"
                    documentType="informeCliente"
                    downloadFileName="StockPorDeposito.pdf"
                />
            ) : null}
        </Box>
    );
};

export default StockPorDeposito;
